import { useMutation } from '@tanstack/react-query'
import { usePushStatus, useRegisterPush, useUnregisterPush } from './api'

/**
 * O aviso no navegador (RN-ASS-32, T39): com a permissão da pessoa, este navegador recebe "Amanda
 * assinou" mesmo com a aba fechada. Só a stack do Supabase: a inscrição vai à API, que guarda no banco e
 * envia pela chave VAPID da própria Edge Function.
 */

export type PushSupport = 'unsupported' | 'denied' | 'ready'

export function pushSupport(): PushSupport {
  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    return 'unsupported'
  }
  return Notification.permission === 'denied' ? 'denied' : 'ready'
}

/** A chave VAPID em base64url → os bytes que o navegador pede. */
export function applicationServerKey(base64url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4)
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

export const PUSH_MESSAGES = {
  denied:
    'O navegador está bloqueando avisos deste site. Libere nas configurações do navegador e tente de novo.',
  unavailable: 'O aviso no navegador não está configurado neste ambiente.',
  on: 'Aviso ligado. Este navegador avisa quando uma paciente assinar.',
  off: 'Aviso desligado neste navegador.',
} as const

export function usePushNotifications() {
  const support = pushSupport()
  const status = usePushStatus(support !== 'unsupported')
  const register = useRegisterPush()
  const unregister = useUnregisterPush()

  const enable = useMutation({
    mutationFn: async () => {
      if (support === 'denied') throw new Error(PUSH_MESSAGES.denied)
      const key = status.data?.vapidPublicKey
      if (!key) throw new Error(PUSH_MESSAGES.unavailable)
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') throw new Error(PUSH_MESSAGES.denied)
      const registration = await navigator.serviceWorker.register('/sw.js')
      const ready = await navigator.serviceWorker.ready
      const subscription =
        (await ready.pushManager.getSubscription()) ??
        (await (ready ?? registration).pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey(key) as BufferSource,
        }))
      const json = subscription.toJSON()
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error('A inscrição do navegador veio incompleta. Tente de novo.')
      }
      await register.mutateAsync({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        userAgent: navigator.userAgent.slice(0, 500),
      })
    },
  })

  const disable = useMutation({
    mutationFn: async () => {
      const registration = await navigator.serviceWorker.getRegistration('/sw.js')
      const subscription = await registration?.pushManager.getSubscription()
      if (subscription) {
        await unregister.mutateAsync(subscription.endpoint)
        await subscription.unsubscribe()
      }
    },
  })

  return {
    support,
    isAvailable: status.data?.isAvailable ?? false,
    isSubscribed: status.data?.isSubscribed ?? false,
    isLoading: status.isPending && support !== 'unsupported',
    enable,
    disable,
  }
}
